import React, { useMemo, useState } from 'react';
import {
  Users, UserCheck, LayoutGrid, List, Calendar, Eye, Car, Phone,
  MessageSquare, Clock, CheckCircle2, Flame, X, Save, DollarSign,
  ClipboardList, AlertCircle, Bot, User, History, ArrowRight, Tag
} from 'lucide-react';
import { CustomerFilters } from './CustomerFilters';
import { CustomerRow } from './CustomerRow';
import { customerAPI } from '../../services/api';

const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: '#60a5fa' },
  { id: 'contacting', label: 'Contacting', color: '#38bdf8' },
  { id: 'engaged', label: 'Engaged', color: '#a78bfa' },
  { id: 'appraisal', label: 'Appraisal', color: '#f59e0b' },
  { id: 'offer', label: 'Offer Made', color: '#fb7185' },
  { id: 'appointment', label: 'Appointment', color: '#14b8a6' },
  { id: 'acquired', label: 'Acquired', color: '#22c55e' }
];

const OUTCOME_STAGES = [
  { id: 'follow-up', label: 'Follow Up', color: '#fbbf24' },
  { id: 'lost', label: 'Lost', color: '#ef4444' }
];

const ALL_STAGE_OPTIONS = [...PIPELINE_STAGES, ...OUTCOME_STAGES];
const STAGE_LABEL = Object.fromEntries(ALL_STAGE_OPTIONS.map(stage => [stage.id, stage.label]));

const normalizeStage = (customer) => {
  const raw = String(customer?.acquisitionStatus || customer?.status || '').toLowerCase().trim();
  if (raw === 'follow-up' || raw === 'follow up' || raw === 'followup') return 'follow-up';
  if (raw === 'lost' || raw.includes('declined') || raw.includes('dead') || raw === 'not-interested') return 'lost';
  if (raw === 'acquired' || raw.includes('purchased') || raw.includes('completed')) return 'acquired';
  if (raw.includes('appointment')) return 'appointment';
  if (raw.includes('offer')) return 'offer';
  if (raw.includes('apprais')) return 'appraisal';
  if (raw.includes('hot') || raw.includes('interest') || customer?.tradeInInterest) return 'engaged';
  if (raw.includes('contact') || raw.includes('messag') || raw.includes('conversation')) return 'contacting';
  return 'new';
};

const getVehicle = (customer) => customer?.vehicle ||
  [customer?.vehicleYear, customer?.vehicleMake, customer?.vehicleModel, customer?.vehicleTrim]
    .filter(Boolean).join(' ') || 'Vehicle not specified';

const getPhone = (customer) => customer?.phone || customer?.cPhone || customer?.cellPhone || 'No phone';
const customerKey = (customer) => String(customer?.id ?? customer?._id ?? customer?.vin ?? customer?.name);
const moneyValue = (value) => value === null || value === undefined ? '' : String(value).replace(/[^0-9.]/g, '');

const formatMoney = (value) => {
  const numeric = Number(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) && numeric > 0 ? `$${numeric.toLocaleString()}` : '';
};

const formatActivityTime = (value) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const appendActivity = (customer, event) => {
  const existing = Array.isArray(customer?.acquisitionActivity) ? customer.acquisitionActivity : [];
  const entry = {
    id: event.id || `activity-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: event.timestamp || new Date().toISOString(),
    ...event
  };
  return [...existing, entry].slice(-250);
};

const buildTimeline = (customer) => {
  if (!customer) return [];
  const events = [];

  (Array.isArray(customer.acquisitionActivity) ? customer.acquisitionActivity : []).forEach(event => {
    events.push({
      ...event,
      timelineType: event.type || 'activity',
      timestamp: event.timestamp || event.createdAt || customer.createdAt
    });
  });

  (Array.isArray(customer.conversations) ? customer.conversations : []).forEach((message, index) => {
    const sender = String(message.sender || '').toLowerCase();
    const actor = sender === 'customer' ? 'Customer' : sender === 'ai' ? 'Sydney AI' : 'Dealership Team';
    events.push({
      id: `message-${message.twilioSid || message.timestamp || index}`,
      timelineType: 'message',
      actor,
      source: sender === 'ai' ? 'Sydney' : sender === 'customer' ? 'SMS' : 'CRM',
      title: sender === 'customer' ? 'Customer replied' : sender === 'ai' ? 'Sydney sent message' : 'Message sent',
      detail: message.message || message.body || '',
      timestamp: message.timestamp || message.createdAt
    });
  });

  if (customer.createdAt) {
    events.push({
      id: `created-${customer.id || customer.createdAt}`,
      timelineType: 'created',
      actor: 'VinCentrix',
      title: 'Opportunity created',
      detail: customer.source ? `Source: ${customer.source}` : 'Added to acquisition workflow',
      timestamp: customer.createdAt
    });
  }

  return events
    .filter(event => event.timestamp)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

export const CustomerTable = ({
  customers,
  onViewDetails,
  onViewMessages,
  onEdit,
  onDelete,
  onSchedule,
  onAddNew,
  theme,
  colors,
  filters = {},
  customersLoading = false,
  showActionButtons = true
}) => {
  const [viewMode, setViewMode] = useState('pipeline');
  const [localUpdates, setLocalUpdates] = useState({});
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  const {
    searchQuery = '',
    setSearchQuery = () => {},
    statusFilter = 'all',
    setStatusFilter = () => {}
  } = filters;

  const workingCustomers = useMemo(() => customers.map(customer => ({
    ...customer,
    ...(localUpdates[customerKey(customer)] || {})
  })), [customers, localUpdates]);

  const pipeline = useMemo(() => {
    const grouped = Object.fromEntries([...PIPELINE_STAGES, ...OUTCOME_STAGES].map(stage => [stage.id, []]));
    workingCustomers.forEach(customer => grouped[normalizeStage(customer)].push(customer));
    return grouped;
  }, [workingCustomers]);

  const acquiredCount = pipeline.acquired.length;
  const lostCount = pipeline.lost.length;
  const followUpCount = pipeline['follow-up'].length;
  const activeCount = Math.max(0, workingCustomers.length - acquiredCount - lostCount);
  const engagedCount = pipeline.engaged.length + pipeline.appraisal.length + pipeline.offer.length + pipeline.appointment.length;
  const timeline = useMemo(() => buildTimeline(selectedOpportunity), [selectedOpportunity]);

  const glass = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.035) 100%)',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 10px 35px rgba(0,0,0,.32)',
    backdropFilter: 'blur(28px) saturate(140%)',
    WebkitBackdropFilter: 'blur(28px) saturate(140%)'
  };

  const persistUpdate = async (customer, updates, successText = 'Saved') => {
    const id = customer?.id;
    if (id === undefined || id === null) throw new Error('This opportunity does not have a valid customer ID.');
    setSavingId(id);
    setSaveError('');
    setSaveMessage('');
    try {
      const response = await customerAPI.update(id, updates);
      const saved = response?.customer || { ...customer, ...updates };
      setLocalUpdates(prev => ({
        ...prev,
        [customerKey(customer)]: { ...(prev[customerKey(customer)] || {}), ...saved, ...updates }
      }));
      setSaveMessage(successText);
      return saved;
    } catch (error) {
      setSaveError(error.message || 'Unable to save changes.');
      throw error;
    } finally {
      setSavingId(null);
    }
  };

  const moveOpportunity = async (customer, stage) => {
    const key = customerKey(customer);
    const previous = localUpdates[key] || {};
    const fromStage = normalizeStage(customer);
    if (fromStage === stage) return;

    const activity = appendActivity(customer, {
      type: 'stage-change',
      actor: 'Dealership Team',
      source: 'manual',
      fromStage,
      toStage: stage,
      title: `Moved to ${STAGE_LABEL[stage] || stage}`,
      detail: `${STAGE_LABEL[fromStage] || fromStage} → ${STAGE_LABEL[stage] || stage}`
    });

    const optimistic = { acquisitionStatus: stage, acquisitionActivity: activity, acquisitionUpdatedAt: new Date().toISOString() };
    setLocalUpdates(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...optimistic } }));

    try {
      await persistUpdate(customer, optimistic, `Moved to ${STAGE_LABEL[stage] || stage}`);
    } catch (error) {
      setLocalUpdates(prev => ({ ...prev, [key]: previous }));
    }
  };

  const openOpportunity = (customer) => {
    const current = { ...customer, ...(localUpdates[customerKey(customer)] || {}) };
    setSelectedOpportunity(current);
    setDraft({
      acquisitionStatus: normalizeStage(current),
      nextAction: current.nextAction || '',
      followUpDate: current.followUpDate || '',
      assignedTo: current.assignedTo || current.assignedAppraiser || '',
      source: current.source || '',
      marketValue: moneyValue(current.marketValue),
      dealerAppraisal: moneyValue(current.dealerAppraisal),
      customerAskingPrice: moneyValue(current.customerAskingPrice),
      offerAmount: moneyValue(current.offerAmount || current.currentOffer),
      purchasePrice: moneyValue(current.purchasePrice),
      lostReason: current.lostReason || '',
      notes: current.notes || ''
    });
    setSaveMessage('');
    setSaveError('');
  };

  const saveOpportunity = async () => {
    if (!selectedOpportunity) return;
    const beforeStage = normalizeStage(selectedOpportunity);
    const changed = [];

    if (beforeStage !== draft.acquisitionStatus) changed.push(`Stage: ${STAGE_LABEL[beforeStage]} → ${STAGE_LABEL[draft.acquisitionStatus]}`);
    if ((selectedOpportunity.nextAction || '') !== (draft.nextAction || '')) changed.push('Next action updated');
    if (moneyValue(selectedOpportunity.offerAmount || selectedOpportunity.currentOffer) !== moneyValue(draft.offerAmount)) changed.push(`Offer ${formatMoney(draft.offerAmount) || 'cleared'}`);
    if (moneyValue(selectedOpportunity.dealerAppraisal) !== moneyValue(draft.dealerAppraisal)) changed.push(`Appraisal ${formatMoney(draft.dealerAppraisal) || 'cleared'}`);
    if (moneyValue(selectedOpportunity.purchasePrice) !== moneyValue(draft.purchasePrice)) changed.push(`Purchase price ${formatMoney(draft.purchasePrice) || 'cleared'}`);
    if ((selectedOpportunity.followUpDate || '') !== (draft.followUpDate || '')) changed.push('Follow-up date updated');
    if ((selectedOpportunity.assignedTo || selectedOpportunity.assignedAppraiser || '') !== (draft.assignedTo || '')) changed.push(`Assigned to ${draft.assignedTo || 'Unassigned'}`);

    const activity = appendActivity(selectedOpportunity, {
      type: beforeStage !== draft.acquisitionStatus ? 'stage-change' : 'opportunity-update',
      actor: 'Dealership Team',
      source: 'manual',
      fromStage: beforeStage,
      toStage: draft.acquisitionStatus,
      title: beforeStage !== draft.acquisitionStatus ? `Moved to ${STAGE_LABEL[draft.acquisitionStatus]}` : 'Opportunity updated',
      detail: changed.length ? changed.join(' • ') : 'Opportunity details saved'
    });

    const payload = {
      acquisitionStatus: draft.acquisitionStatus,
      nextAction: draft.nextAction,
      followUpDate: draft.followUpDate,
      assignedTo: draft.assignedTo,
      assignedAppraiser: draft.assignedTo,
      source: draft.source,
      marketValue: draft.marketValue,
      dealerAppraisal: draft.dealerAppraisal,
      customerAskingPrice: draft.customerAskingPrice,
      offerAmount: draft.offerAmount,
      currentOffer: draft.offerAmount,
      purchasePrice: draft.purchasePrice,
      lostReason: draft.lostReason,
      notes: draft.notes,
      acquisitionActivity: activity,
      acquisitionUpdatedAt: new Date().toISOString()
    };

    try {
      const saved = await persistUpdate(selectedOpportunity, payload, 'Opportunity saved');
      setSelectedOpportunity({ ...selectedOpportunity, ...saved, ...payload });
    } catch (error) {
      // Error is displayed in drawer.
    }
  };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 9,
    border: '1px solid rgba(255,255,255,.12)', background: 'rgba(255,255,255,.05)',
    color: '#fff', fontSize: 13, outline: 'none'
  };

  const renderPipelineCard = (customer) => (
    <article key={customerKey(customer)} className="vc-pipeline-card" style={{ background:'linear-gradient(145deg,rgba(15,23,42,.96),rgba(8,15,28,.96))', border:'1px solid rgba(255,255,255,.08)', borderRadius:13, padding:12 }}>
      <div style={{ color:'#fff', fontSize:13, fontWeight:800, lineHeight:1.35 }}>{getVehicle(customer)}</div>
      {customer.vin && <div style={{ color:'rgba(255,255,255,.34)', fontSize:10, marginTop:3, letterSpacing:.4 }}>{customer.vin}</div>}
      <div style={{ height:1, background:'rgba(255,255,255,.06)', margin:'10px 0' }} />
      <div style={{ color:'rgba(255,255,255,.78)', fontSize:12, fontWeight:700 }}>{customer.name || 'Unknown owner'}</div>
      <div style={{ display:'flex',alignItems:'center',gap:5,color:'rgba(255,255,255,.42)',fontSize:11,marginTop:5 }}><Phone size={11}/>{getPhone(customer)}</div>
      {customer.nextAction && <div style={{marginTop:8,padding:'7px 8px',borderRadius:7,background:'rgba(96,165,250,.07)',color:'rgba(219,234,254,.72)',fontSize:10,lineHeight:1.35}}><ClipboardList size={10} style={{marginRight:5,verticalAlign:'-1px'}}/> {customer.nextAction}</div>}
      {customer.sydneyPipelineAutomated && customer.sydneyPipelineReason && (
        <div style={{marginTop:7,padding:'6px 8px',borderRadius:7,background:'rgba(139,92,246,.08)',color:'#c4b5fd',fontSize:9,lineHeight:1.35,border:'1px solid rgba(139,92,246,.14)'}}>
          <Bot size={10} style={{marginRight:5,verticalAlign:'-1px'}}/> Sydney: {customer.sydneyPipelineReason}
        </div>
      )}
      <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginTop:10 }}>
        {customer.tradeInInterest && <span style={{fontSize:9,fontWeight:800,color:'#c4b5fd',background:'rgba(139,92,246,.14)',border:'1px solid rgba(139,92,246,.24)',padding:'3px 6px',borderRadius:6}}>INTERESTED</span>}
        {customer.source && <span style={{fontSize:9,fontWeight:800,color:'#93c5fd',background:'rgba(59,130,246,.12)',border:'1px solid rgba(59,130,246,.2)',padding:'3px 6px',borderRadius:6}}>{String(customer.source).toUpperCase()}</span>}
        {(customer.assignedTo || customer.assignedAppraiser) && <span style={{fontSize:9,fontWeight:800,color:'#fde68a',background:'rgba(245,158,11,.1)',border:'1px solid rgba(245,158,11,.2)',padding:'3px 6px',borderRadius:6}}>{customer.assignedTo || customer.assignedAppraiser}</span>}
      </div>
      <select
        aria-label={`Move ${customer.name || 'opportunity'} to stage`}
        value={normalizeStage(customer)}
        disabled={savingId === customer.id}
        onChange={(e) => moveOpportunity(customer, e.target.value)}
        style={{width:'100%',marginTop:10,padding:'7px 8px',borderRadius:7,border:'1px solid rgba(255,255,255,.09)',background:'#0b1322',color:'rgba(255,255,255,.68)',fontSize:10,fontWeight:700,cursor:'pointer'}}
      >
        {ALL_STAGE_OPTIONS.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
      <div style={{ display:'grid', gridTemplateColumns:onSchedule ? '1fr 1fr' : '1fr', gap:6, marginTop:8 }}>
        <button onClick={() => openOpportunity(customer)} style={{padding:'8px',borderRadius:8,border:'1px solid rgba(96,165,250,.22)',background:'rgba(59,130,246,.11)',color:'#bfdbfe',fontSize:10,fontWeight:800,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5}}><Eye size={12}/> Open</button>
        {onSchedule && <button onClick={() => onSchedule(customer)} style={{padding:'8px',borderRadius:8,border:'1px solid rgba(20,184,166,.22)',background:'rgba(20,184,166,.1)',color:'#99f6e4',fontSize:10,fontWeight:800,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5}}><Calendar size={12}/> Schedule</button>}
      </div>
    </article>
  );

  return (
    <div className="vc-acquisition-workspace" style={{ animation: 'tabFadeIn .45s ease-out' }}>
      <style>{`
        .vc-pipeline-board { display:grid; grid-template-columns:repeat(7,minmax(230px,1fr)); gap:14px; min-width:1740px; }
        .vc-pipeline-scroll { overflow-x:auto; padding-bottom:8px; -webkit-overflow-scrolling:touch; }
        .vc-pipeline-card { transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease; }
        .vc-pipeline-card:hover { transform:translateY(-2px); border-color:rgba(96,165,250,.45)!important; box-shadow:0 12px 28px rgba(0,0,0,.35),0 0 0 1px rgba(59,130,246,.08); }
        .vc-opportunity-drawer { animation:vcDrawerIn .22s ease-out; }
        .vc-timeline-event:last-child .vc-timeline-line { display:none; }
        @keyframes vcDrawerIn { from { transform:translateX(28px); opacity:.3; } to { transform:translateX(0); opacity:1; } }
        @media (max-width:768px) {
          .vc-acquisition-header { padding:20px!important; }
          .vc-acquisition-header-row { align-items:flex-start!important; flex-direction:column!important; }
          .vc-acquisition-actions { width:100%; display:grid!important; grid-template-columns:1fr 1fr; }
          .vc-acquisition-actions button { width:100%; justify-content:center; }
          .vc-acquisition-stats { grid-template-columns:1fr 1fr!important; }
          .vc-pipeline-scroll { overflow:visible; }
          .vc-pipeline-board { display:block; min-width:0; }
          .vc-pipeline-column { margin-bottom:14px; }
          .vc-opportunity-drawer { width:100%!important; max-width:none!important; }
          .vc-drawer-grid { grid-template-columns:1fr!important; }
        }
      `}</style>

      <div className="vc-acquisition-header" style={{ ...glass, borderRadius:22, padding:'26px 30px', marginBottom:18, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:'auto -10% -70% auto', width:320, height:320, borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,.16),transparent 68%)', pointerEvents:'none' }} />
        <div className="vc-acquisition-header-row" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:18, position:'relative' }}>
          <div>
            <div style={{ fontSize:11, letterSpacing:2.2, textTransform:'uppercase', color:'#60a5fa', fontWeight:800, marginBottom:8 }}>VinCentrix Acquisition CRM</div>
            <h2 style={{ margin:0, fontSize:28, color:'#fff', display:'flex', alignItems:'center', gap:10 }}><Flame size={27} color="#60a5fa" /> Acquisition Pipeline</h2>
            <p style={{ margin:'7px 0 0', color:'rgba(255,255,255,.52)', fontSize:13 }}>Work every vehicle from first opportunity through purchase.</p>
          </div>
          <div className="vc-acquisition-actions" style={{ display:'flex', gap:10, alignItems:'center' }}>
            <button onClick={() => setViewMode('pipeline')} style={{ padding:'11px 14px', borderRadius:10, border:'1px solid rgba(96,165,250,.32)', background:viewMode === 'pipeline' ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : 'rgba(255,255,255,.05)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:7, fontWeight:700 }}><LayoutGrid size={16}/> Pipeline</button>
            <button onClick={() => setViewMode('customers')} style={{ padding:'11px 14px', borderRadius:10, border:'1px solid rgba(255,255,255,.12)', background:viewMode === 'customers' ? 'rgba(96,165,250,.18)' : 'rgba(255,255,255,.04)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:7, fontWeight:700 }}><List size={16}/> Customers</button>
            {onAddNew && <button onClick={onAddNew} style={{ padding:'11px 15px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#3b82f6,#2563eb)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:7, fontWeight:800, boxShadow:'0 0 20px rgba(59,130,246,.28)' }}><UserCheck size={16}/> Add</button>}
          </div>
        </div>
      </div>

      {viewMode === 'pipeline' ? (
        <>
          <div className="vc-acquisition-stats" style={{ display:'grid', gridTemplateColumns:'repeat(4,minmax(0,1fr))', gap:12, marginBottom:16 }}>
            {[
              ['Total Opportunities', workingCustomers.length, Car, '#60a5fa'],
              ['Active Pipeline', activeCount, Clock, '#38bdf8'],
              ['Engaged+', engagedCount, MessageSquare, '#a78bfa'],
              ['Acquired', acquiredCount, CheckCircle2, '#22c55e']
            ].map(([label,value,Icon,color]) => (
              <div key={label} style={{ ...glass, borderRadius:15, padding:'16px 18px', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38,height:38,borderRadius:10,background:`${color}18`,display:'grid',placeItems:'center',border:`1px solid ${color}35` }}><Icon size={18} color={color}/></div>
                <div><div style={{ color:'rgba(255,255,255,.5)', fontSize:11, textTransform:'uppercase', letterSpacing:.8 }}>{label}</div><div style={{ color:'#fff', fontSize:24, fontWeight:800, marginTop:2 }}>{value}</div></div>
              </div>
            ))}
          </div>

          {(followUpCount > 0 || lostCount > 0) && (
            <div style={{ display:'flex', gap:8, margin:'-4px 0 13px', flexWrap:'wrap' }}>
              <span style={{fontSize:11,color:'#fde68a',background:'rgba(245,158,11,.08)',border:'1px solid rgba(245,158,11,.2)',padding:'5px 9px',borderRadius:999}}>Follow Up: {followUpCount}</span>
              <span style={{fontSize:11,color:'#fca5a5',background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.2)',padding:'5px 9px',borderRadius:999}}>Lost: {lostCount}</span>
            </div>
          )}

          <div className="vc-pipeline-scroll">
            <div className="vc-pipeline-board">
              {PIPELINE_STAGES.map(stage => (
                <section key={stage.id} className="vc-pipeline-column" style={{ ...glass, borderRadius:16, padding:10, minHeight:260 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 6px 11px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:8,height:8,borderRadius:'50%',background:stage.color,boxShadow:`0 0 12px ${stage.color}` }}/><span style={{ color:'#fff', fontSize:12, fontWeight:800, letterSpacing:.4 }}>{stage.label}</span></div>
                    <span style={{ minWidth:25,textAlign:'center',padding:'3px 7px',borderRadius:999,background:'rgba(255,255,255,.07)',color:'rgba(255,255,255,.65)',fontSize:11,fontWeight:800 }}>{pipeline[stage.id].length}</span>
                  </div>
                  <div style={{ display:'grid', gap:9 }}>
                    {pipeline[stage.id].length === 0 ? <div style={{ padding:'24px 8px', textAlign:'center', color:'rgba(255,255,255,.28)', fontSize:12 }}>No opportunities</div> : pipeline[stage.id].map(renderPipelineCard)}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <CustomerFilters customers={workingCustomers} searchQuery={searchQuery} setSearchQuery={setSearchQuery} statusFilter={statusFilter} setStatusFilter={setStatusFilter} theme={theme} colors={colors} />
          <div style={{ ...glass, borderRadius:18, overflow:'hidden', marginTop:14 }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:'rgba(255,255,255,.045)', borderBottom:'1px solid rgba(255,255,255,.08)' }}>{['Customer','Phone','Vehicle','Status','Actions'].map(label => <th key={label} style={{padding:'15px 20px',textAlign:'left',fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:theme.textMuted}}>{label}</th>)}</tr></thead>
                <tbody>
                  {customersLoading ? <tr><td colSpan="5" style={{padding:40,textAlign:'center',color:theme.textSecondary}}>Loading customers...</td></tr> : workingCustomers.length === 0 ? <tr><td colSpan="5" style={{padding:50,textAlign:'center',color:theme.textSecondary}}><Users size={40} style={{opacity:.3,marginBottom:10}}/><div>No customers yet</div></td></tr> : workingCustomers.map((customer,index) => (
                    <CustomerRow key={customerKey(customer)} customer={customer} onView={() => onViewDetails(customer)} onEdit={() => onEdit && onEdit(customer)} onDelete={() => onDelete && onDelete(customer.id || customer._id)} onSchedule={onSchedule ? () => onSchedule(customer) : undefined} theme={theme} colors={colors} isLast={index === workingCustomers.length - 1} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selectedOpportunity && (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedOpportunity(null); }} style={{position:'fixed',inset:0,zIndex:1200,background:'rgba(0,0,0,.58)',backdropFilter:'blur(5px)',display:'flex',justifyContent:'flex-end'}}>
          <aside className="vc-opportunity-drawer" style={{width:'min(620px,96vw)',height:'100%',overflowY:'auto',background:'linear-gradient(180deg,#07101f 0%,#050b15 100%)',borderLeft:'1px solid rgba(96,165,250,.22)',boxShadow:'-24px 0 60px rgba(0,0,0,.5)',padding:22,boxSizing:'border-box'}}>
            <div style={{display:'flex',justifyContent:'space-between',gap:14,alignItems:'flex-start',paddingBottom:18,borderBottom:'1px solid rgba(255,255,255,.08)'}}>
              <div>
                <div style={{fontSize:10,fontWeight:900,letterSpacing:1.8,color:'#60a5fa',textTransform:'uppercase'}}>Acquisition Opportunity</div>
                <h3 style={{margin:'6px 0 2px',color:'#fff',fontSize:20}}>{getVehicle(selectedOpportunity)}</h3>
                <div style={{color:'rgba(255,255,255,.45)',fontSize:12}}>{selectedOpportunity.name || 'Unknown owner'} • {getPhone(selectedOpportunity)}</div>
                {selectedOpportunity.vin && <div style={{color:'rgba(255,255,255,.28)',fontSize:10,marginTop:5,letterSpacing:.5}}>VIN {selectedOpportunity.vin}</div>}
              </div>
              <button onClick={() => setSelectedOpportunity(null)} aria-label="Close opportunity" style={{border:'1px solid rgba(255,255,255,.1)',background:'rgba(255,255,255,.05)',color:'#fff',width:34,height:34,borderRadius:9,cursor:'pointer',display:'grid',placeItems:'center'}}><X size={17}/></button>
            </div>

            {selectedOpportunity.sydneyPipelineAutomated && selectedOpportunity.sydneyPipelineReason && (
              <div style={{marginTop:14,padding:'11px 12px',borderRadius:10,background:'rgba(139,92,246,.09)',border:'1px solid rgba(139,92,246,.2)',color:'#ddd6fe',fontSize:11,lineHeight:1.45}}><Bot size={14} style={{verticalAlign:'-2px',marginRight:7}}/><strong>Sydney automation:</strong> {selectedOpportunity.sydneyPipelineReason}</div>
            )}

            <div className="vc-drawer-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:18}}>
              <label style={{fontSize:11,color:'rgba(255,255,255,.55)'}}>Stage<select value={draft.acquisitionStatus || 'new'} onChange={e => setDraft({...draft,acquisitionStatus:e.target.value})} style={{...fieldStyle,marginTop:6,background:'#0b1322'}}>{ALL_STAGE_OPTIONS.map(stage => <option key={stage.id} value={stage.id}>{stage.label}</option>)}</select></label>
              <label style={{fontSize:11,color:'rgba(255,255,255,.55)'}}>Assigned Buyer<input value={draft.assignedTo || ''} onChange={e => setDraft({...draft,assignedTo:e.target.value})} placeholder="Unassigned" style={{...fieldStyle,marginTop:6}} /></label>
              <label style={{fontSize:11,color:'rgba(255,255,255,.55)'}}>Source<input value={draft.source || ''} onChange={e => setDraft({...draft,source:e.target.value})} placeholder="Service Drive" style={{...fieldStyle,marginTop:6}} /></label>
              <label style={{fontSize:11,color:'rgba(255,255,255,.55)'}}>Follow-Up Date<input type="date" value={draft.followUpDate || ''} onChange={e => setDraft({...draft,followUpDate:e.target.value})} style={{...fieldStyle,marginTop:6,colorScheme:'dark'}} /></label>
            </div>

            <label style={{display:'block',fontSize:11,color:'rgba(255,255,255,.55)',marginTop:12}}>Next Action<input value={draft.nextAction || ''} onChange={e => setDraft({...draft,nextAction:e.target.value})} placeholder="What happens next?" style={{...fieldStyle,marginTop:6}} /></label>

            <div style={{marginTop:20,paddingTop:17,borderTop:'1px solid rgba(255,255,255,.08)'}}>
              <div style={{display:'flex',alignItems:'center',gap:7,color:'#fff',fontSize:12,fontWeight:850,marginBottom:11}}><DollarSign size={15} color="#60a5fa"/> Acquisition Numbers</div>
              <div className="vc-drawer-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                {[
                  ['Market Value','marketValue'],['Dealer Appraisal','dealerAppraisal'],['Customer Asking','customerAskingPrice'],['Offer Made','offerAmount'],['Purchased For','purchasePrice']
                ].map(([label,key]) => <label key={key} style={{fontSize:11,color:'rgba(255,255,255,.55)'}}>{label}<input inputMode="decimal" value={draft[key] || ''} onChange={e => setDraft({...draft,[key]:moneyValue(e.target.value)})} placeholder="$0" style={{...fieldStyle,marginTop:6}} /></label>)}
              </div>
            </div>

            {draft.acquisitionStatus === 'lost' && <label style={{display:'block',fontSize:11,color:'#fca5a5',marginTop:14}}>Lost Reason<input value={draft.lostReason || ''} onChange={e => setDraft({...draft,lostReason:e.target.value})} placeholder="Why did we lose this opportunity?" style={{...fieldStyle,marginTop:6,borderColor:'rgba(239,68,68,.25)'}} /></label>}

            <label style={{display:'block',fontSize:11,color:'rgba(255,255,255,.55)',marginTop:14}}>Acquisition Notes<textarea value={draft.notes || ''} onChange={e => setDraft({...draft,notes:e.target.value})} rows={4} placeholder="Internal notes about this acquisition..." style={{...fieldStyle,marginTop:6,resize:'vertical',fontFamily:'inherit'}} /></label>

            {saveError && <div style={{marginTop:12,padding:'9px 10px',borderRadius:8,background:'rgba(239,68,68,.1)',color:'#fca5a5',fontSize:11,display:'flex',gap:6,alignItems:'center'}}><AlertCircle size={13}/>{saveError}</div>}
            {saveMessage && !saveError && <div style={{marginTop:12,padding:'9px 10px',borderRadius:8,background:'rgba(34,197,94,.09)',color:'#86efac',fontSize:11,display:'flex',gap:6,alignItems:'center'}}><CheckCircle2 size={13}/>{saveMessage}</div>}

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:9,marginTop:18,paddingTop:18,borderTop:'1px solid rgba(255,255,255,.08)'}}>
              <button onClick={() => onViewDetails && onViewDetails(selectedOpportunity)} style={{padding:'11px',borderRadius:9,border:'1px solid rgba(255,255,255,.1)',background:'rgba(255,255,255,.05)',color:'#dbeafe',fontSize:11,fontWeight:800,cursor:'pointer'}}>Full Customer Record</button>
              <button onClick={saveOpportunity} disabled={savingId === selectedOpportunity.id} style={{padding:'11px',borderRadius:9,border:'none',background:'linear-gradient(135deg,#3b82f6,#2563eb)',color:'#fff',fontSize:11,fontWeight:900,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6,opacity:savingId === selectedOpportunity.id ? .7 : 1}}><Save size={14}/>{savingId === selectedOpportunity.id ? 'Saving...' : 'Save Opportunity'}</button>
            </div>

            <div style={{marginTop:24,paddingTop:20,borderTop:'1px solid rgba(255,255,255,.08)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{display:'flex',alignItems:'center',gap:7,color:'#fff',fontSize:13,fontWeight:900}}><History size={16} color="#60a5fa"/> Activity Timeline</div>
                <span style={{fontSize:10,color:'rgba(255,255,255,.35)'}}>{timeline.length} events</span>
              </div>
              {timeline.length === 0 ? (
                <div style={{padding:'22px 12px',textAlign:'center',color:'rgba(255,255,255,.3)',fontSize:11}}>No activity recorded yet.</div>
              ) : (
                <div>
                  {timeline.map((event, index) => {
                    const isSydney = String(event.actor || '').toLowerCase().includes('sydney');
                    const isCustomer = event.actor === 'Customer';
                    const iconColor = isSydney ? '#a78bfa' : isCustomer ? '#38bdf8' : event.timelineType === 'stage-change' ? '#60a5fa' : '#94a3b8';
                    const Icon = isSydney ? Bot : isCustomer ? User : event.timelineType === 'stage-change' ? ArrowRight : event.timelineType === 'message' ? MessageSquare : Tag;
                    return (
                      <div key={event.id || `${event.timestamp}-${index}`} className="vc-timeline-event" style={{display:'grid',gridTemplateColumns:'30px 1fr',gap:10,position:'relative'}}>
                        <div style={{position:'relative',display:'flex',justifyContent:'center'}}>
                          <div style={{width:26,height:26,borderRadius:9,background:`${iconColor}15`,border:`1px solid ${iconColor}35`,display:'grid',placeItems:'center',zIndex:1}}><Icon size={12} color={iconColor}/></div>
                          <div className="vc-timeline-line" style={{position:'absolute',top:27,bottom:0,width:1,background:'rgba(255,255,255,.09)'}} />
                        </div>
                        <div style={{paddingBottom:16,minWidth:0}}>
                          <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'baseline'}}>
                            <div style={{color:'#e5e7eb',fontSize:11,fontWeight:800}}>{event.title || 'Activity'}</div>
                            <div style={{color:'rgba(255,255,255,.28)',fontSize:9,whiteSpace:'nowrap'}}>{formatActivityTime(event.timestamp)}</div>
                          </div>
                          <div style={{color:iconColor,fontSize:9,fontWeight:800,marginTop:2}}>{event.actor || 'VinCentrix'}{event.source ? ` • ${event.source}` : ''}</div>
                          {event.fromStage && event.toStage && event.fromStage !== event.toStage && <div style={{color:'rgba(191,219,254,.7)',fontSize:9,marginTop:4}}>{STAGE_LABEL[event.fromStage] || event.fromStage} → {STAGE_LABEL[event.toStage] || event.toStage}</div>}
                          {event.detail && <div style={{color:'rgba(255,255,255,.5)',fontSize:10,lineHeight:1.45,marginTop:5,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{event.detail}</div>}
                          {event.relatedMessage && event.timelineType !== 'message' && <div style={{marginTop:6,padding:'7px 8px',borderRadius:7,background:'rgba(255,255,255,.035)',color:'rgba(255,255,255,.38)',fontSize:9,lineHeight:1.4}}>“{event.relatedMessage}”</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};
